import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import AuthImage from "@/components/AuthImage";
import { toast } from "sonner";
import { Star, Trash2, Upload } from "lucide-react";
import { ZONES } from "@/lib/extensions";

const emptyBoat = {
  brand: "", model: "", year: "", type: "motor",
  length: "", beam: "", capacity: "", engine: "",
  country: "", location: "", port: "",
  estimated_value: "", annual_cost: "", mooring_cost: "", other_costs: "",
  general_condition: "", last_maintenance: "", next_maintenance: "",
  description: "", ownership_mode: "coownership", status: "draft", listing_status: "unlisted",
};

const num = (v) => v === "" || v === null || v === undefined ? null : Number(v);
const cleanPayload = (f) => ({
  ...f,
  year: num(f.year), length: num(f.length), beam: num(f.beam), capacity: num(f.capacity),
  estimated_value: num(f.estimated_value) ?? 0, annual_cost: num(f.annual_cost) ?? 0,
  mooring_cost: num(f.mooring_cost) ?? 0, other_costs: num(f.other_costs) ?? 0,
});

export default function BoatForm() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyBoat);
  const [images, setImages] = useState([]);
  const [ownership, setOwnership] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({ desired_owners: 2, distribution: "", comments: "", contact_email: "" });
  const fileRef = useRef(null);

  useEffect(() => {
    if (user?.email) setCustom((c) => ({ ...c, contact_email: user.email }));
  }, [user?.email]);

  useEffect(() => {
    if (id) {
      api.get(`/boats/${id}`).then(({ data }) => {
        setForm({ ...emptyBoat, ...data, year: data.year ?? "", length: data.length ?? "", beam: data.beam ?? "", capacity: data.capacity ?? "",
          estimated_value: data.estimated_value ?? "", annual_cost: data.annual_cost ?? "",
          mooring_cost: data.mooring_cost ?? "", other_costs: data.other_costs ?? "" });
        setImages(data.images || []);
        setOwnership(data.ownership);
      }).catch(() => navigate("/mis-embarcaciones"));
    }
  }, [id, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ev = num(form.estimated_value) || 0;
  const sharePrice = Math.round((ev / 4) * 100) / 100;

  const save = async (statusOverride) => {
    setBusy(true);
    try {
      const payload = cleanPayload({ ...form, status: statusOverride || form.status });
      if (id) {
        const { data } = await api.patch(`/boats/${id}`, payload);
        setOwnership(data.ownership);
        toast.success(t.boats.updated);
      } else {
        const { data } = await api.post("/boats", payload);
        toast.success(t.boats.created);
        navigate(`/mis-embarcaciones/${data.id}/editar`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    if (!id) { toast.error(t.boats.save + " →"); return; }
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post(`/boats/${id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((imgs) => [...imgs, data]);
      } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const setPrimary = async (imageId) => {
    await api.patch(`/boat-images/${imageId}`, { is_primary: true });
    setImages((imgs) => imgs.map((i) => ({ ...i, is_primary: i.id === imageId })));
  };

  const removeImage = async (imageId) => {
    await api.delete(`/boat-images/${imageId}`);
    setImages((imgs) => imgs.filter((i) => i.id !== imageId));
  };

  const sendCustomRequest = async () => {
    if (!custom.contact_email) return toast.error("Email");
    await api.post("/ownership-requests", { ...custom, boat_id: id, desired_owners: Number(custom.desired_owners) });
    toast.success(t.boats.ownership.requestSent);
    setShowCustom(false);
  };

  const requestPublication = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/boats/${id}/request-publication`);
      setForm((f) => ({ ...f, listing_status: data.listing_status }));
      toast.success(t.boats.publishRequested);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-14 lg:py-20">
      <p className="eyebrow">Bevela</p>
      <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-[#0B192C]" data-testid="boat-form-title">
        {id ? t.boats.editTitle : t.boats.newTitle}
      </h1>

      {/* BASIC */}
      <Section title={t.boats.section.basic}>
        <Grid cols={2}>
          <Field label={t.boats.f.brand}><input className="input-elegant" value={form.brand} onChange={set("brand")} data-testid="boat-brand"/></Field>
          <Field label={t.boats.f.model}><input className="input-elegant" value={form.model} onChange={set("model")} data-testid="boat-model"/></Field>
          <Field label={t.boats.f.year}><input type="number" className="input-elegant" value={form.year} onChange={set("year")} data-testid="boat-year"/></Field>
          <Field label={t.boats.f.type}>
            <select className="input-elegant" value={form.type} onChange={set("type")} data-testid="boat-type">
              {["sailboat","motor","catamaran","other"].map((k) => <option key={k} value={k}>{t.boats.types[k]}</option>)}
            </select>
          </Field>
          <Field label={t.boats.f.length}><input type="number" step="0.1" className="input-elegant" value={form.length} onChange={set("length")} data-testid="boat-length"/></Field>
          <Field label={t.boats.f.beam}><input type="number" step="0.1" className="input-elegant" value={form.beam} onChange={set("beam")} data-testid="boat-beam"/></Field>
          <Field label={t.boats.f.capacity}><input type="number" className="input-elegant" value={form.capacity} onChange={set("capacity")} data-testid="boat-capacity"/></Field>
          <Field label={t.boats.f.engine}><input className="input-elegant" value={form.engine} onChange={set("engine")} data-testid="boat-engine"/></Field>
        </Grid>
      </Section>

      <Section title={t.boats.section.location}>
        <Grid cols={3}>
          <Field label={t.boats.f.country}><input className="input-elegant" value={form.country} onChange={set("country")} data-testid="boat-country"/></Field>
          <Field label={t.boats.f.location}>
            <select className="input-elegant" value={form.location} onChange={set("location")} data-testid="boat-location">
              <option value="">—</option>
              {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              {form.location && !ZONES.includes(form.location) && <option value={form.location}>{form.location}</option>}
            </select>
          </Field>
          <Field label={t.boats.f.port}><input className="input-elegant" value={form.port} onChange={set("port")} data-testid="boat-port"/></Field>
        </Grid>
      </Section>

      <Section title={t.boats.section.economic}>
        <Grid cols={2}>
          <Field label={t.boats.f.estimatedValue}><input type="number" className="input-elegant" value={form.estimated_value} onChange={set("estimated_value")} data-testid="boat-value"/></Field>
          <Field label={t.boats.f.annualCost}><input type="number" className="input-elegant" value={form.annual_cost} onChange={set("annual_cost")} data-testid="boat-annual-cost"/></Field>
          <Field label={t.boats.f.mooringCost}><input type="number" className="input-elegant" value={form.mooring_cost} onChange={set("mooring_cost")} data-testid="boat-mooring"/></Field>
          <Field label={t.boats.f.otherCosts}><input type="number" className="input-elegant" value={form.other_costs} onChange={set("other_costs")} data-testid="boat-other"/></Field>
        </Grid>
      </Section>

      <Section title={t.boats.section.condition}>
        <Grid cols={2}>
          <Field label={t.boats.f.generalCondition}>
            <select className="input-elegant" value={form.general_condition} onChange={set("general_condition")} data-testid="boat-condition">
              <option value="">—</option>
              {["excellent","good","fair","needs_improvements"].map((k) => <option key={k} value={k}>{t.boats.conditions[k]}</option>)}
            </select>
          </Field>
          <Field label={t.boats.f.status}>
            <select className="input-elegant" value={form.status} onChange={set("status")} data-testid="boat-status">
              {["draft","reserved","sold","inactive"].map((k) => <option key={k} value={k}>{t.boats.status[k]}</option>)}
            </select>
          </Field>
          <Field label={t.boats.ownershipMode}>
            <select className="input-elegant" value={form.ownership_mode} onChange={set("ownership_mode")} data-testid="boat-ownership-mode">
              {["coownership","full"].map((k) => <option key={k} value={k}>{t.boats.ownershipModes[k]}</option>)}
            </select>
          </Field>
          <Field label={t.boats.f.lastMaintenance}><input type="date" className="input-elegant" value={form.last_maintenance} onChange={set("last_maintenance")} data-testid="boat-last-maint"/></Field>
          <Field label={t.boats.f.nextMaintenance}><input type="date" className="input-elegant" value={form.next_maintenance} onChange={set("next_maintenance")} data-testid="boat-next-maint"/></Field>
        </Grid>
        <Field label={t.boats.f.description}>
          <textarea rows={4} className="input-elegant resize-none" value={form.description} onChange={set("description")} data-testid="boat-description"/>
        </Field>
      </Section>

      {/* PHOTOS */}
      <Section title={t.boats.section.photos}>
        {!id ? (
          <p className="text-sm text-slate-500">— {t.boats.save} —</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="boat-images">
              {images.map((img) => (
                <div key={img.id} className="relative group aspect-[4/3] rounded-xl overflow-hidden border border-slate-100">
                  <AuthImage path={img.storage_path} className="w-full h-full object-cover" />
                  {img.is_primary && (
                    <span className="absolute top-2 left-2 text-[10px] font-display uppercase tracking-widest px-2 py-1 rounded-full bg-[#C2A878] text-white flex items-center gap-1">
                      <Star className="h-3 w-3" /> {t.boats.photos.primary}
                    </span>
                  )}
                  <div className="absolute bottom-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!img.is_primary && (
                      <button onClick={() => setPrimary(img.id)} className="p-1.5 rounded-full bg-white/90 hover:bg-white" title={t.boats.photos.markPrimary} data-testid={`img-primary-${img.id}`}>
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => removeImage(img.id)} className="p-1.5 rounded-full bg-white/90 hover:bg-white text-red-500" data-testid={`img-remove-${img.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <label className="aspect-[4/3] border-2 border-dashed border-slate-200 rounded-xl grid place-items-center cursor-pointer hover:border-slate-400 transition-colors">
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="boat-image-input"/>
                <div className="text-center px-4">
                  <Upload className="h-5 w-5 mx-auto text-slate-400" />
                  <p className="mt-2 text-xs text-slate-500">{t.boats.photos.drag}</p>
                  <p className="mt-1 text-[10px] text-slate-400">{t.boats.photos.formats}</p>
                </div>
              </label>
            </div>
          </>
        )}
      </Section>

      {/* OWNERSHIP */}
      <Section title={t.boats.section.ownership}>
        <div className="rounded-2xl border border-slate-100 p-6 lg:p-8 bg-[#F8F9FA]">
          <p className="eyebrow">{t.boats.ownership.standard}</p>
          <p className="mt-1 font-serif text-2xl text-[#0B192C]">{t.boats.ownership.standardDesc}</p>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label={t.boats.ownership.boatValue} value={`€${ev.toLocaleString()}`} testid="ownership-value" />
            <Metric label={t.boats.ownership.owners} value="4" testid="ownership-owners" />
            <Metric label={t.boats.ownership.percentage} value="25%" testid="ownership-pct" />
            <Metric label={t.boats.ownership.sharePrice} value={`€${sharePrice.toLocaleString()}`} testid="ownership-share-price" highlight />
          </div>

          <div className="mt-6 flex items-end gap-1 h-3">
            {[0,1,2,3].map((i) => (
              <div key={i} className="flex-1 rounded-full bg-[#C2A878]/90 h-full" />
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <p className="text-sm text-slate-600">{t.boats.ownership.different}</p>
            <button type="button" className="btn-outline text-sm" onClick={() => setShowCustom((v) => !v)} data-testid="ownership-consult">
              {t.boats.ownership.consult}
            </button>
          </div>

          {showCustom && (
            <div className="mt-6 border-t border-slate-200 pt-6 grid sm:grid-cols-2 gap-4" data-testid="ownership-custom-form">
              <Field label={t.boats.ownership.desiredOwners}>
                <input type="number" min="2" className="input-elegant" value={custom.desired_owners} onChange={(e) => setCustom((c) => ({ ...c, desired_owners: e.target.value }))} data-testid="custom-desired-owners"/>
              </Field>
              <Field label={t.boats.ownership.contactEmail}>
                <input type="email" className="input-elegant" value={custom.contact_email} onChange={(e) => setCustom((c) => ({ ...c, contact_email: e.target.value }))} data-testid="custom-contact-email"/>
              </Field>
              <div className="sm:col-span-2">
                <Field label={t.boats.ownership.distribution}>
                  <input className="input-elegant" value={custom.distribution} onChange={(e) => setCustom((c) => ({ ...c, distribution: e.target.value }))} data-testid="custom-distribution"/>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label={t.boats.ownership.comments}>
                  <textarea rows={3} className="input-elegant resize-none" value={custom.comments} onChange={(e) => setCustom((c) => ({ ...c, comments: e.target.value }))} data-testid="custom-comments"/>
                </Field>
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <button onClick={sendCustomRequest} className="btn-primary text-sm" data-testid="custom-submit">
                  {t.boats.ownership.sendRequest}
                </button>
              </div>
            </div>
          )}
        </div>
      </Section>

      <div className="mt-14 space-y-6">
        {id && (
          <div className="rounded-2xl border border-slate-100 bg-[#F8F9FA] p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="boat-publish-block">
            <div className="min-w-0">
              <p className="eyebrow">{t.boats.listingLabel}</p>
              <p className="mt-1 font-serif text-lg text-[#0B192C]" data-testid="boat-listing-status">{t.boats.listing[form.listing_status] || t.boats.listing.unlisted}</p>
              <p className="mt-1 text-xs text-slate-500 max-w-md">{t.boats.publishInfo}</p>
            </div>
            {form.listing_status !== "published" && form.listing_status !== "pending_review" && (
              <button onClick={requestPublication} disabled={busy} className="btn-outline text-sm disabled:opacity-40" data-testid="boat-request-publish">{t.boats.requestPublish}</button>
            )}
          </div>
        )}
        <div className="flex justify-between items-center flex-wrap gap-4">
          <button onClick={() => navigate(-1)} className="btn-ghost text-sm">←</button>
          <button onClick={() => save()} disabled={busy} className="btn-primary disabled:opacity-40" data-testid="boat-save">{id ? t.boats.saveChanges : t.boats.save}</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-14">
      <h2 className="font-display text-xs tracking-[0.24em] uppercase text-slate-400 mb-6">{title}</h2>
      <div className="space-y-5">{children}</div>
    </section>
  );
}
function Grid({ cols = 2, children }) {
  const c = cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  return <div className={`grid ${c} gap-5`}>{children}</div>;
}
function Field({ label, children }) {
  return <div><label className="label-elegant">{label}</label>{children}</div>;
}
function Metric({ label, value, testid, highlight }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`mt-2 font-serif text-xl ${highlight ? "text-[#0B192C]" : "text-slate-700"}`}>{value}</p>
    </div>
  );
}
